/**
 * ArchivePane - Archive Panel Container
 *
 * Container component for the archive panel with tabs:
 * - Browser: Tree view of conversations and content
 * - Search: Unified semantic search (transcripts + content)
 * - Clusters: Discovered topic clusters
 * - Import: Archive import wizard
 *
 * Shows setup/error panels when archive is not connected.
 *
 * @module @humanizer/studio/components/archive/ArchivePane
 */

import React, { useCallback, useRef, useState } from 'react';
import { usePanels, usePanelState } from '../../contexts/PanelContext';
import { useBufferSync, type ArchiveNode } from '../../contexts/BufferSyncContext';
import { useMedia, type MediaItem } from '../../contexts/MediaContext';
import { useArchiveOptional } from '../../contexts/ArchiveContext';
import { ArchiveBrowser } from './ArchiveBrowser';
import { ArchiveSearch } from './ArchiveSearch';
import { ClusterBrowser } from './ClusterBrowser';
import { ImportView } from './ImportView';
import { MediaBrowser } from './MediaBrowser';
import { DriftView } from './DriftView';
import { CuratorView, type CuratorSessionState, type FindBuffer } from './CuratorView';
import { ArchiveSetupPanel } from './ArchiveSetupPanel';
import { ArchiveErrorPanel } from './ArchiveErrorPanel';
import { ProjectBrowser } from './ProjectBrowser';
import type { ApiMediaItem } from '../../types/media';
import { resolveMediaUrl } from '../../utils/media-url';
import { SubsetTool } from '../tools/SubsetTool';
import { GraphBrowser } from './GraphBrowser';
import { ErrorBoundary } from '../ui/ErrorBoundary';
import { createLogger } from '../../utils/debug';

const log = createLogger('ArchivePane');

// ═══════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════

export interface ArchivePaneProps {
  /** Archive ID to display */
  archiveId?: string;
  /** Called when a content item is selected */
  onSelectContent?: (contentId: string, type: 'node' | 'transcript') => void;
  /** Called when similar search is requested */
  onSearchSimilar?: (embedding: number[], sourceId: string) => void;
  /** Called when dissimilar search is requested */
  onSearchDissimilar?: (embedding: number[], sourceId: string) => void;
  /** Called when search wants to highlight text in workspace */
  onHighlightRequested?: (text: string) => void;
  /** Called when search loads a thread and should navigate to a specific message */
  onNavigateToMessage?: (index: number) => void;
  /** Called when user wants to navigate to a conversation from media detail */
  onNavigateToConversation?: (conversationId: string) => void;
  /** Called when a saved curator search is opened from ProjectBrowser (M5) */
  onCuratorSearchOpen?: (findBuffer: FindBuffer) => void;
  /** Request workspace to switch view mode (from ProjectBrowser chapter actions) */
  onViewModeRequest?: (mode: 'stack' | 'outline' | 'draft') => void;
  /** Called when user clicks "Open in Workspace" on a transcription version */
  onOpenTranscriptionInWorkspace?: (text: string, modelLabel: string) => void;
  /** Optional class name */
  className?: string;
}

// Tab configuration
const TABS = [
  { id: 'browser', label: 'Browse', icon: '📂' },
  { id: 'project', label: 'Project', icon: '📁' },
  { id: 'search', label: 'Search', icon: '🔍' },
  { id: 'clusters', label: 'Clusters', icon: '🎯' },
  { id: 'media', label: 'Media', icon: '🎬' },
  { id: 'drift', label: 'Drift', icon: '\u{1F300}' },
  { id: 'curator', label: 'Curator', icon: '\u{1F441}' },
  { id: 'graph', label: 'Graph', icon: '\u{1F517}' },
  { id: 'subset', label: 'Subset', icon: '📦' },
  { id: 'import', label: 'Import', icon: '📥' },
] as const;

// ═══════════════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

export function ArchivePane({
  archiveId,
  onSelectContent,
  onSearchSimilar,
  onSearchDissimilar,
  onHighlightRequested,
  onNavigateToMessage,
  onNavigateToConversation,
  onCuratorSearchOpen,
  onViewModeRequest,
  onOpenTranscriptionInWorkspace,
  className = '',
}: ArchivePaneProps): React.ReactElement {
  const panelState = usePanelState();
  const { setArchiveTab, toggleArchive } = usePanels();
  const { importArchiveNode, sessionId, openNodeInContext } = useBufferSync();
  const { addItem, selectItem, deselectAll } = useMedia();

  // Archive connection state (optional - may not be wrapped in provider yet)
  const archive = useArchiveOptional();

  // Curator state persistence — ref survives tab switches (CuratorView unmounts/remounts)
  const curatorStateRef = useRef<CuratorSessionState>();

  // Setup mode toggle (for when user dismisses error and wants to reconfigure)
  const [showSetup, setShowSetup] = useState(false);

  const handleTabChange = useCallback(
    (tabId: typeof TABS[number]['id']) => {
      setArchiveTab(tabId);
    },
    [setArchiveTab]
  );

  // Handle content selection notification from child tabs.
  // Note: The actual import to workspace is done by the child component
  // (e.g. ArchiveBrowser.importMessage) before this callback fires.
  // This is purely a notification hook for external consumers.
  const handleSelectContent = useCallback(
    (contentId: string, type: 'node' | 'transcript') => {
      onSelectContent?.(contentId, type);
    },
    [onSelectContent]
  );

  // Handle media selection - import to workspace with rich detail card
  const handleMediaSelect = useCallback(
    async (item: ApiMediaItem) => {
      if (!sessionId) return;
      try {
        const mediaType = item.mimeType.startsWith('image/') ? 'image' : item.mimeType.split('/')[0];
        const url = item.sourcePointer
          ? resolveMediaUrl(item.sourcePointer)
          : `/api/media/serve/${item.id}`;

        const lines: string[] = [];

        // Embed: image markdown or HTML5 audio/video player
        if (mediaType === 'image') {
          lines.push(`![${item.filename || 'media'}](${url})`);
        } else if (mediaType === 'audio') {
          lines.push(`<audio controls preload="metadata" src="${url}">Your browser does not support audio.</audio>`);
        } else if (mediaType === 'video') {
          lines.push(`<video controls preload="metadata" src="${url}">Your browser does not support video.</video>`);
        }
        lines.push('');

        // Metadata table
        lines.push('| | |');
        lines.push('|---|---|');
        if (item.filename) lines.push(`| **File** | ${item.filename} |`);
        lines.push(`| **Type** | ${item.mimeType} |`);
        if (item.size) lines.push(`| **Size** | ${(item.size / 1024).toFixed(1)} KB |`);
        if (item.width && item.height) lines.push(`| **Dimensions** | ${item.width} × ${item.height} |`);
        if (item.sourceType) lines.push(`| **Source** | \`${item.sourceType}\` |`);

        // Show both created and imported dates when available
        if (item.sourceCreatedAt) {
          lines.push(`| **Created** | ${new Date(item.sourceCreatedAt).toLocaleString()} |`);
        }
        if (item.createdAt) {
          lines.push(`| **Imported** | ${new Date(item.createdAt).toLocaleString()} |`);
        }

        // Existing data sections
        if (item.dallePrompt) lines.push(`\n> **DALL-E Prompt:** ${item.dallePrompt}`);
        if (item.caption) lines.push(`\n> **Caption:** ${item.caption}`);
        if (item.transcript) lines.push(`\n> **Transcript:** ${item.transcript}`);

        // Source conversation link
        if (item.conversationId) {
          lines.push(`\n---\n[View source conversation →](#conversation:${item.conversationId})`);
        }

        const node: ArchiveNode = {
          id: item.id,
          text: lines.join('\n'),
          type: 'media',
          sourceType: item.sourceType || 'archive',
        };
        await importArchiveNode(node);

        // Also register in MediaContext so TranscribeTool can use it
        const ctxType = item.mimeType.startsWith('image/') ? 'image'
          : item.mimeType.startsWith('audio/') ? 'audio'
          : item.mimeType.startsWith('video/') ? 'video' : 'image';
        const mediaItem: MediaItem = {
          id: item.id,
          archiveId: archiveId || '',
          type: ctxType as MediaItem['type'],
          filename: item.filename || item.contentHash,
          mimeType: item.mimeType,
          size: item.size || 0,
          duration: item.duration ?? undefined,
          dimensions: item.width && item.height ? { width: item.width, height: item.height } : undefined,
          sourceUrl: url,
          conversationId: item.conversationId ?? undefined,
          createdAt: new Date(item.sourceCreatedAt || item.createdAt),
          transcriptionCount: 0,
        };
        addItem(mediaItem);
        deselectAll();
        selectItem(item.id);
      } catch (error) {
        log.error('Failed to import media to buffer:', error);
      }
    },
    [sessionId, importArchiveNode, archiveId, addItem, selectItem, deselectAll]
  );

  // Handle navigating to source conversation from media
  const handleNavigateToConversation = useCallback(
    (conversationId: string) => {
      if (onNavigateToConversation) {
        onNavigateToConversation(conversationId);
      }
      setArchiveTab('browser');
    },
    [onNavigateToConversation, setArchiveTab]
  );

  // Handle login request from setup/error panels
  const handleLoginRequest = useCallback(() => {
    // This would trigger the login modal from App.tsx
    // Parent component should pass this handler
  }, []);

  // Render tab content - checks archive status first
  const renderTabContent = () => {
    // ELECTRON: Always show setup panel unless connected
    if (archive?.platform === 'electron') {
      if (archive.status !== 'connected') {
        return (
          <ArchiveSetupPanel
            onLoginRequest={handleLoginRequest}
          />
        );
      }
      // Fall through to show normal content when connected
    }

    // Check archive status if provider is available (web mode)
    if (archive) {
      // Show loading spinner while checking
      if (archive.status === 'checking') {
        return (
          <div className="archive-pane__loading" aria-live="polite" role="status">
            <div className="archive-pane__spinner" />
            <p>Connecting to archive...</p>
          </div>
        );
      }

      // Show setup panel when not configured (web or explicit setup mode)
      if (archive.status === 'setup-required' || showSetup) {
        return (
          <ArchiveSetupPanel
            onLoginRequest={handleLoginRequest}
          />
        );
      }

      // Show error panel when connection failed
      if (archive.status === 'error') {
        return (
          <ArchiveErrorPanel
            onSetup={() => setShowSetup(true)}
            onLogin={handleLoginRequest}
          />
        );
      }

      // Only continue if explicitly connected
      if (archive.status !== 'connected') {
        return (
          <div className="archive-pane__loading">
            <p>Archive status: {archive.status}</p>
          </div>
        );
      }
    }

    // Archive is connected (or no ArchiveContext for legacy compatibility) - show normal content
    switch (panelState.archiveTab) {
      case 'browser':
        return (
          <ArchiveBrowser
            archiveId={archiveId}
            onSelectContent={handleSelectContent}
          />
        );
      case 'project':
        return (
          <ProjectBrowser
            onBufferSelect={(bufferId) => openNodeInContext({ nodeId: bufferId })}
            onCuratorSearchOpen={onCuratorSearchOpen}
            onViewModeRequest={onViewModeRequest}
          />
        );
      case 'search':
        return (
          <ArchiveSearch
            archiveId={archiveId}
            onSelectContent={handleSelectContent}
            onHighlightRequested={onHighlightRequested}
            onNavigateToMessage={onNavigateToMessage}
          />
        );
      case 'clusters':
        return (
          <ClusterBrowser
            archiveId={archiveId}
            onSearchSimilar={onSearchSimilar}
          />
        );
      case 'media':
        return (
          <MediaBrowser
            onSelectMedia={handleMediaSelect}
            onOpenThread={(threadId) => openNodeInContext({ nodeId: threadId })}
            onOpenTranscriptionInWorkspace={onOpenTranscriptionInWorkspace}
          />
        );
      case 'drift':
        return <DriftView />;
      case 'curator':
        return (
          <CuratorView
            archiveId={archiveId}
            onSelectContent={(nodeId) => handleSelectContent(nodeId, 'node')}
            onOpenNode={(nodeId) => openNodeInContext({ nodeId })}
            sessionStateRef={curatorStateRef}
          />
        );
      case 'graph':
        return (
          <GraphBrowser
            onOpenContent={(nodeId) => openNodeInContext({ nodeId })}
          />
        );
      case 'subset':
        return (
          <ErrorBoundary fallback="panel" label="Subset tool">
            <SubsetTool />
          </ErrorBoundary>
        );
      case 'import':
        return (
          <ImportView
            onImportComplete={() => {
              // Switch back to browser after import
              setArchiveTab('browser');
            }}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className={`archive-pane ${className}`}>
      {/* Panel Header */}
      <div className="panel__header">
        <h2 className="panel__title">Archive</h2>
        <div className="panel__actions">
          {!panelState.isMobile && (
            <button
              className="panel__collapse-btn"
              onClick={toggleArchive}
              aria-label="Collapse archive panel"
              title="Collapse (Cmd+[)"
            >
              ‹
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="panel__tabs" role="tablist" aria-label="Archive views">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            className={`panel__tab ${panelState.archiveTab === tab.id ? 'panel__tab--active' : ''}`}
            role="tab"
            aria-selected={panelState.archiveTab === tab.id}
            aria-controls={`archive-panel-${tab.id}`}
            onClick={() => handleTabChange(tab.id)}
          >
            <span aria-hidden="true">{tab.icon}</span>
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div
        className="panel__content"
        role="tabpanel"
        id={`archive-panel-${panelState.archiveTab}`}
        aria-labelledby={`archive-tab-${panelState.archiveTab}`}
      >
        {renderTabContent()}
      </div>
    </div>
  );
}

export default ArchivePane;
